import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Menu from "../../components/menu/Menu";

const STATUS_STYLES = {
  online: "bg-emerald-100 text-emerald-700 border-emerald-200",
  degraded: "bg-amber-100 text-amber-800 border-amber-200",
  offline: "bg-red-100 text-red-700 border-red-200",
  unknown: "bg-slate-100 text-slate-600 border-slate-200",
  disabled: "bg-slate-100 text-slate-400 border-slate-200",
};

const PRIORITY_STYLES = {
  P0: "bg-red-50 text-red-700 border-red-200",
  P1: "bg-orange-50 text-orange-700 border-orange-200",
  P2: "bg-blue-50 text-blue-700 border-blue-200",
  P3: "bg-slate-50 text-slate-600 border-slate-200",
};

function formatStatus(status) {
  if (!status) return "Unknown";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function formatRelative(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const diffMs = Date.now() - date.getTime();
  const future = diffMs < 0;
  const abs = Math.abs(diffMs);
  const seconds = Math.floor(abs / 1000);
  if (seconds < 60) return future ? `in ${seconds}s` : `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return future ? `in ${minutes}m` : `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return future ? `in ${hours}h` : `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return future ? `in ${days}d` : `${days}d ago`;
}

function exactTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

function StatusBadge({ status }) {
  const normalized = status || "unknown";
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-semibold ${
        STATUS_STYLES[normalized] || STATUS_STYLES.unknown
      }`}
    >
      {formatStatus(normalized)}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const value = priority || "P2";
  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-md border text-xs font-semibold ${
        PRIORITY_STYLES[value] || PRIORITY_STYLES.P2
      }`}
    >
      {value}
    </span>
  );
}

function SummaryCard({ label, value, status }) {
  const topBorder = {
    online: "border-t-emerald-500",
    degraded: "border-t-amber-500",
    offline: "border-t-red-500",
    unknown: "border-t-slate-400",
    incidents: "border-t-rose-600",
  }[status] || "border-t-slate-400";

  return (
    <div className={`bg-white border border-slate-200 border-t-4 ${topBorder} rounded-xl p-4 shadow-sm`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-2xl font-semibold text-slate-900 mt-2">{value || 0}</div>
    </div>
  );
}

const MonitorReport = (props) => {
  const { actSidebar, checkLoggedIn } = props;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({});
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [incidentFilter, setIncidentFilter] = useState("Unresolved");
  const [incidentHistory, setIncidentHistory] = useState([]);
  const [serviceDetails, setServiceDetails] = useState({});
  const [checkHistories, setCheckHistories] = useState({});
  const [actionId, setActionId] = useState(null);

  const loadMonitoring = useCallback(async (silent = false) => {
    const userData = JSON.parse(localStorage.getItem("userData") || "null");
    if (!userData?.token) {
      checkLoggedIn(false);
      return;
    }

    if (silent) setRefreshing(true);
    else setLoading(true);
    setError("");

    try {
      axios.defaults.headers.common["Authorization"] = userData.token;
      const [result, historyResult] = await Promise.all([
        axios.get(`${window.ajaxLink}cloud_dashboard/monitoring/overview`),
        axios.get(`${window.ajaxLink}cloud_dashboard/monitoring/incidents?limit=500`),
      ]);
      const payload = result?.data?.responseData;
      if (!payload) throw new Error("Monitoring API returned no data.");
      setData(payload);
      setIncidentHistory(historyResult?.data?.responseData?.incidents || []);
      setLastRefreshedAt(new Date());
    } catch (err) {
      const message =
        err?.response?.data?.responseMessage ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load monitoring report.";
      setError(message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [checkLoggedIn]);

  useEffect(() => {
    actSidebar(true);
    const userData = JSON.parse(localStorage.getItem("userData") || "null");
    if (!userData) {
      checkLoggedIn(false);
      return undefined;
    }

    loadMonitoring();
    const timer = setInterval(() => loadMonitoring(true), 30000);
    return () => clearInterval(timer);
    // Same mount-only page initialization pattern used by the existing admin pages.
  }, []);

  const services = data?.services || [];
  const incidents = useMemo(() => incidentHistory.filter((incident) =>
    incidentFilter === "All" ||
    (incidentFilter === "Unresolved" && ["open", "acknowledged"].includes(incident.status.toLowerCase())) ||
    incident.status.toLowerCase() === incidentFilter.toLowerCase()
  ), [incidentHistory, incidentFilter]);
  const summary = data?.summary || {};

  const sortedServices = useMemo(() => {
    const priority = { P0: 0, P1: 1, P2: 2, P3: 3 };
    return services.filter((service) =>
      (priorityFilter === "All" || service.criticality === priorityFilter) &&
      (statusFilter === "All" || service.status === statusFilter.toLowerCase())
    ).sort((a, b) => {
      const p = (priority[a.criticality] ?? 9) - (priority[b.criticality] ?? 9);
      if (p !== 0) return p;
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
  }, [services, priorityFilter, statusFilter]);

  const toggleService = (serviceKey) => {
    setExpanded((current) => ({ ...current, [serviceKey]: !current[serviceKey] }));
  };

  useEffect(() => {
    const serviceKeys = Object.keys(expanded).filter((key) => expanded[key]);
    if (!serviceKeys.length) return undefined;
    const refreshDetails = () => serviceKeys.forEach((serviceKey) => {
      axios.get(`${window.ajaxLink}cloud_dashboard/monitoring/services/${encodeURIComponent(serviceKey)}`)
        .then((result) => setServiceDetails((current) => ({ ...current, [serviceKey]: result.data.responseData })))
        .catch(() => setError("Failed to load service metrics."));
    });
    refreshDetails();
    const timer = setInterval(refreshDetails, 30000);
    return () => clearInterval(timer);
  }, [expanded]);

  const toggleCheckHistory = async (checkId) => {
    if (checkHistories[checkId]) {
      setCheckHistories((current) => ({ ...current, [checkId]: null }));
      return;
    }
    try {
      const result = await axios.get(`${window.ajaxLink}cloud_dashboard/monitoring/checks/${checkId}/history?limit=30`);
      setCheckHistories((current) => ({ ...current, [checkId]: result.data.responseData.history || [] }));
    } catch (err) {
      setError("Failed to load check history.");
    }
  };

  const updateIncident = async (incident, action) => {
    const resolution = action === "resolve" ? window.prompt("Resolution (required):") : null;
    if (action === "resolve" && !resolution?.trim()) return;
    setActionId(incident.id);
    try {
      await axios.post(`${window.ajaxLink}cloud_dashboard/monitoring/incidents/${incident.id}/${action}`,
        action === "resolve" ? { resolution: resolution.trim() } : {});
      await loadMonitoring(true);
    } catch (err) {
      setError(err?.response?.data?.responseData?.message || "Failed to update incident.");
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="max-h-screen overflow-y-auto w-full bg-slate-50 min-h-screen">
      <Menu title="Monitoring Report" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 font-['Inter',sans-serif]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">System Monitoring</h1>
            <p className="text-sm text-slate-500 mt-1">
              Current health of backend infrastructure and critical scheduled jobs.
              {data?.environment ? ` Environment: ${data.environment}.` : ""}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">
              {lastRefreshedAt ? `Updated ${lastRefreshedAt.toLocaleTimeString()}` : "Not refreshed yet"}
            </span>
            <button
              type="button"
              onClick={() => loadMonitoring(true)}
              disabled={refreshing}
              className="px-4 py-2 bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200 rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
            >
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <SummaryCard label="Online" value={summary.online} status="online" />
          <SummaryCard label="Degraded" value={summary.degraded} status="degraded" />
          <SummaryCard label="Offline" value={summary.offline} status="offline" />
          <SummaryCard label="Unknown" value={summary.unknown} status="unknown" />
          <SummaryCard label="Unresolved Incidents" value={summary.openIncidents} status="incidents" />
        </div>

        <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6">
          <div className="px-5 py-4 border-b border-slate-200">
            <h2 className="text-sm font-semibold text-slate-800">Services</h2>
            <p className="text-xs text-slate-500 mt-1">Click a service to expand its individual checks.</p>
            <div className="flex flex-wrap gap-3 mt-3">
              <label className="text-xs text-slate-600">Priority{" "}
                <select value={priorityFilter} onChange={(event) => setPriorityFilter(event.target.value)} className="border border-slate-200 rounded px-2 py-1">
                  {["All", "P0", "P1", "P2"].map((value) => <option key={value}>{value}</option>)}
                </select>
              </label>
              <label className="text-xs text-slate-600">Status{" "}
                <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="border border-slate-200 rounded px-2 py-1">
                  {["All", "Online", "Degraded", "Offline", "Unknown"].map((value) => <option key={value}>{value}</option>)}
                </select>
              </label>
            </div>
          </div>

          {loading ? (
            <div className="py-14 text-center text-sm text-slate-500">Loading monitoring data...</div>
          ) : sortedServices.length === 0 ? (
            <div className="py-14 text-center text-sm text-slate-500">No monitoring services are registered.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-5 py-3">Service</th>
                    <th className="px-4 py-3">Priority</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Last Checked</th>
                    <th className="px-4 py-3" title="Most recent success of an individual check; not the last time the whole service was healthy.">Latest Check Success</th>
                    <th className="px-4 py-3">Last Failure</th>
                    <th className="px-4 py-3 text-right">Incidents</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sortedServices.map((service) => {
                    const isExpanded = !!expanded[service.serviceKey];
                    return (
                      <React.Fragment key={service.serviceKey}>
                        <tr
                          className="hover:bg-slate-50 cursor-pointer"
                          onClick={() => toggleService(service.serviceKey)}
                        >
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2">
                              <span className="text-slate-400 text-xs">{isExpanded ? "▼" : "▶"}</span>
                              <div>
                                <div className="font-medium text-slate-900">{service.name}</div>
                                <div className="text-xs text-slate-400 mt-0.5">{service.serviceKey}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4"><PriorityBadge priority={service.criticality} /></td>
                          <td className="px-4 py-4"><StatusBadge status={service.status} /></td>
                          <td className="px-4 py-4 text-slate-600" title={exactTime(service.lastCheckedAt)}>
                            {formatRelative(service.lastCheckedAt)}
                          </td>
                          <td className="px-4 py-4 text-slate-600" title={exactTime(service.lastCheckSuccessAt || service.lastSuccessAt)}>
                            {formatRelative(service.lastCheckSuccessAt || service.lastSuccessAt)}
                          </td>
                          <td className="px-4 py-4 text-slate-600" title={exactTime(service.lastFailureAt)}>
                            {formatRelative(service.lastFailureAt)}
                          </td>
                          <td className="px-4 py-4 text-right font-medium text-slate-700">
                            {service.openIncidentCount || 0}
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr>
                            <td colSpan="7" className="bg-slate-50 px-6 py-4">
                              {serviceDetails[service.serviceKey]?.metrics24h && (
                                <div className="text-xs text-slate-600 mb-3 flex flex-wrap gap-4">
                                  {(() => { const m = serviceDetails[service.serviceKey].metrics24h; return <>
                                    <span>24h checks: {m.checkCount}</span><span>Failures: {m.failures}</span><span>Degradations: {m.degradations}</span>
                                    <span>Timeouts: {m.timeouts}</span><span>Availability (recorded outages): {m.availability == null ? "—" : `${m.availability}%`}</span>
                                    <span>Average latency: {m.averageLatencyMs == null ? "—" : `${m.averageLatencyMs} ms`}</span>
                                    <span>Last outage (24h): {m.lastOutageAt ? exactTime(m.lastOutageAt) : "—"}</span>
                                  </>; })()}
                                </div>
                              )}
                              <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                                <table className="min-w-full divide-y divide-slate-200 text-xs">
                                  <thead className="bg-slate-50 text-left uppercase tracking-wide text-slate-500">
                                    <tr>
                                      <th className="px-4 py-2.5">Check</th>
                                      <th className="px-4 py-2.5">Status</th>
                                      <th className="px-4 py-2.5">Last Healthy</th>
                                      <th className="px-4 py-2.5">Last Failure</th>
                                      <th className="px-4 py-2.5">Latency</th>
                                      <th className="px-4 py-2.5">Error</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100">
                                    {(service.checks || []).map((check) => (<React.Fragment key={check.id}>
                                      <tr>
                                        <td className="px-4 py-3">
                                          <div className="font-medium text-slate-800">{check.name}</div>
                                          <div className="text-slate-400 mt-0.5">{check.checkKey}</div>
                                          <button type="button" onClick={() => toggleCheckHistory(check.id)} className="text-teal-700 mt-1">{checkHistories[check.id] ? "Hide history" : "View history"}</button>
                                        </td>
                                        <td className="px-4 py-3"><StatusBadge status={check.status} /></td>
                                        <td className="px-4 py-3 text-slate-600" title={exactTime(check.lastSuccessAt)}>
                                          {formatRelative(check.lastSuccessAt)}
                                        </td>
                                        <td className="px-4 py-3 text-slate-600" title={exactTime(check.lastFailureAt)}>
                                          {formatRelative(check.lastFailureAt)}
                                        </td>
                                        <td className="px-4 py-3 text-slate-600">
                                          {check.lastLatencyMs == null ? "—" : `${check.lastLatencyMs} ms`}
                                        </td>
                                        <td className="px-4 py-3 text-slate-600 max-w-xs">
                                          {check.lastErrorCode ? (
                                            <div>
                                              <span className="font-medium">{check.lastErrorCode}</span>
                                              {check.lastErrorMessage && (
                                                <div className="text-slate-400 mt-0.5 truncate" title={check.lastErrorMessage}>
                                                  {check.lastErrorMessage}
                                                </div>
                                              )}
                                            </div>
                                          ) : "—"}
                                        </td>
                                      </tr>
                                      {checkHistories[check.id] && <tr><td colSpan="6" className="px-4 py-3 bg-slate-50">
                                        {checkHistories[check.id].length === 0 ? "No recent check runs." :
                                          checkHistories[check.id].map((run) => <div key={run.id} className="flex gap-3 py-1 border-b border-slate-100">
                                            <span title={exactTime(run.started_at)}>{exactTime(run.started_at)}</span>
                                            <span>{run.status.toUpperCase()}</span><span>{run.latency_ms == null ? "—" : `${run.latency_ms} ms`}</span>
                                            <span>{run.error_code || (run.http_status ? `HTTP ${run.http_status}` : "")}</span>
                                          </div>)}
                                      </td></tr>}
                                    </React.Fragment>))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-slate-800">Incident History</h2>
              <p className="text-xs text-slate-500 mt-1">Incidents created by monitoring thresholds.</p>
            </div>
            <div className="flex items-center gap-3">
              <select aria-label="Incident status" value={incidentFilter} onChange={(event) => setIncidentFilter(event.target.value)} className="border border-slate-200 rounded px-2 py-1 text-xs">
                {["Unresolved", "All", "Open", "Acknowledged", "Resolved"].map((value) => <option key={value}>{value}</option>)}
              </select>
              <span className="text-xs text-slate-400">{incidents.length} incidents</span>
            </div>
          </div>

          {incidents.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500">No matching monitoring incidents.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-5 py-3">Priority</th>
                    <th className="px-4 py-3">Incident</th>
                    <th className="px-4 py-3">Service</th>
                    <th className="px-4 py-3">Started</th>
                    <th className="px-4 py-3">Last Occurrence</th>
                    <th className="px-4 py-3 text-right">Occurrences</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {incidents.map((incident) => (
                    <tr key={incident.id}>
                      <td className="px-5 py-4"><PriorityBadge priority={incident.severity} /></td>
                      <td className="px-4 py-4">
                        <div className="font-medium text-slate-900">{incident.title}</div>
                        <div className="text-xs text-slate-400 mt-0.5">#{incident.id} · {incident.status}</div>
                      </td>
                      <td className="px-4 py-4 text-slate-600">
                        {incident.service_name}
                        {incident.check_name ? <div className="text-xs text-slate-400">{incident.check_name}</div> : null}
                      </td>
                      <td className="px-4 py-4 text-slate-600" title={exactTime(incident.started_at)}>
                        {formatRelative(incident.started_at)}
                      </td>
                      <td className="px-4 py-4 text-slate-600" title={exactTime(incident.last_occurrence_at)}>
                        {formatRelative(incident.last_occurrence_at)}
                      </td>
                      <td className="px-4 py-4 text-right font-medium text-slate-700">
                        {incident.occurrence_count || 1}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {incident.status === "open" && <button type="button" disabled={actionId === incident.id} onClick={() => updateIncident(incident, "acknowledge")} className="text-teal-700 mr-3 disabled:opacity-50">Acknowledge</button>}
                        {incident.status !== "resolved" && <button type="button" disabled={actionId === incident.id} onClick={() => updateIncident(incident, "resolve")} className="text-teal-700 disabled:opacity-50">Resolve</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default MonitorReport;
